import clsx from 'clsx';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import Illustration from '../_assets/illustration.svg?react';
import styles from '../_assets/styles.module.css';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  return (
    <>
      <div
        className="relative isolate flex w-full flex-col overflow-hidden"
        style={{
          background: 'linear-gradient(289.11deg, #D1ECFE -2.21%, #F6FBFF 99.07%)',
        }}
      >
        <div className="container mx-auto flex flex-col md:flex-row">
          <div className="flex-1 2xl:mt-24">
            <Content activeCompetition={props.activeCompetition} winners={props.winners} />
          </div>
          <div className="-z-10 flex flex-1 lg:flex-none lg:pt-10 xl:pr-5">
            <Illustration
              className={clsx(
                'mx-auto mt-auto h-auto w-full max-w-md md:max-w-lg 2xl:max-w-2xl',
                styles.illustration,
              )}
            />
          </div>
        </div>
      </div>

      <Disclaimer />
    </>
  );
};

const Disclaimer = () => (
  <div className="bg-white pt-4">
    <div className="container mx-auto px-5 text-center typography-v1-body4 text-balance">
      <LoanExampleText />
    </div>
  </div>
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="max-w-3xl px-5 pt-6 pb-10 text-[#031839]">
      <h1 className="mt-auto mb-3 text-4xl leading-[1.2] font-bold max-xl:text-balance sm:text-5xl md:-mr-50 xl:mr-0 xl:text-6xl">
        Er det du som våkner <UnderlinedText variant="2">gjeldfri</UnderlinedText>{' '}
        17.&nbsp;november?
      </h1>

      <h2 className="mb-6 typography-v1-body1 text-balance">
        Vi trekker en heldig vinner som får slettet inntil{' '}
        <span className="font-bold text-[#18A0FB]">500&nbsp;000&nbsp;kr</span>
        <br />
        av sin forbruks- og kredittkortgjeld
      </h2>

      <h3 className="mb-2 typography-v1-h4">For å delta:</h3>
      <div className="mb-2 flex flex-col">
        <NumberedList
          renderNumber={(props) => (
            <NumberCircle
              sx={{
                fontSize: 14,
                height: 24,
                backgroundColor: '#FDD183',
                color: '#354661',
                fontWeight: 700,
              }}
              {...props}
            />
          )}
          renderLabel={(props) => <div {...props} className="typography-v1-body4" />}
        >
          <NumberedListItem>Logg inn med bank ID</NumberedListItem>
          <NumberedListItem>Søk om lavere rente</NumberedListItem>
          <NumberedListItem>Delta i trekningen</NumberedListItem>
        </NumberedList>
      </div>

      <a
        href={routes.OCTOBER_2025_COMPETITION_TERMS}
        className="mb-8 block font-semibold text-[#18A0FB] underline"
      >
        Vilkår og betingelser for deltagelse
      </a>

      {ended && (
        <p className="-mt-4 mb-4 typography-v1-h4">
          <span>Denne trekningen er avsluttet. </span>
          <button
            onClick={() => setIsOpen(true)}
            className="mb-0.5 ml-1 font-semibold text-[#18A0FB] underline"
          >
            Se vinneren her
          </button>
        </p>
      )}

      <CTA
        activeCompetition={props.activeCompetition}
        onClick={() => {
          sendUserPortalEvent('oct25_competition_cta_top_clicked');
        }}
      />

      {props.winners && (
        <CompetitionWinnersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          winners={props.winners}
        />
      )}
    </div>
  );
};
